import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { CommonActionPanelComponent } from '../../../common/common-action-panel-component.directive';

import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { EmailBuilderDataService } from '@galaxy/email-ui/email-builder';
import { asReference, PageComponent, Template } from '@galaxy/email-ui/email-library';
import { Context, RuleInterface } from '@vendasta/automata';
import { AppNamespace, OwnerType } from '@vendasta/email-builder';
import { SenderType } from '@vendasta/email';
import { BehaviorSubject, firstValueFrom, Observable, of, retry, Subscription } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { EmailEditorDialogComponent } from '../email-editor/email-editor-dialog.component';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { GalaxyInputModule } from '@vendasta/galaxy/input';
import { MatRadioModule } from '@angular/material/radio';
import { GalaxyWrapModule } from '@vendasta/galaxy/galaxy-wrap';
import { PreviewImageContainerComponent } from '../../../common/preview-image-container/preview-image-container.component';
import { EmailStatsModule } from '@galaxy/email-ui/email-activity';
import { CampaignI18nModule } from '@galaxy/campaign';
import {
  SendEmailToCompanyTaskDefinitionId,
  SendEmailToContactTaskDefinitionId,
  SendEmailToUserTaskDefinitionId,
} from '../../../../component-loader/constants';
import { AutomataContextService } from '@galaxy/automata/shared';
import { EmailUII18nModule } from '@galaxy/email-ui/src/lib/i18n/email-ui-i18n.module';
import { AutomationViewService } from '../../../../../automation/automation-view.service';
import { GalaxyProgressBarModule } from '@vendasta/galaxy/progress-bar';

const Descriptions: { [key: string]: string } = {
  [SendEmailToUserTaskDefinitionId]: 'AUTOMATIONS.EDITOR.TASKS.SEND_HTML_EMAIL_TO_USER.PANEL.DESCRIPTION',
  [SendEmailToContactTaskDefinitionId]: '',
  [SendEmailToCompanyTaskDefinitionId]: 'AUTOMATIONS.EDITOR.TASKS.SEND_HTML_EMAIL_TO_USER.PANEL.DESCRIPTION_COMPANY',
};

@Component({
  selector: 'automata-send-html-email-to-user',
  templateUrl: './send-html-email-to-user.component.html',
  styleUrls: ['./send-html-email-to-user.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatCardModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    GalaxyPipesModule,
    GalaxyTooltipModule,
    GalaxyInputModule,
    MatRadioModule,
    GalaxyWrapModule,
    PreviewImageContainerComponent,
    CampaignI18nModule,
    EmailUII18nModule,
    EmailStatsModule,
    GalaxyProgressBarModule,
  ],
})
export class SendHtmlEmailToUserComponent extends CommonActionPanelComponent implements OnInit, OnDestroy {
  readonly EMAIL_ID_QUERYSTRING_KEY = 'email_to_user_emailId';
  public automationContext = this.automationContextService.currentContext;
  description: string;

  private subscriptions: Subscription[] = [];
  backUrl: string;
  emailOwnerType: OwnerType =
    this.automationContext === Context.AUTOMATION_CONTEXT_PARTNER
      ? OwnerType.OWNER_TYPE_PARTNER
      : OwnerType.OWNER_TYPE_ACCOUNT_GROUP;
  senderType: SenderType =
    this.automationContext === Context.AUTOMATION_CONTEXT_PARTNER
      ? SenderType.SENDER_TYPE_PARTNER
      : SenderType.SENDER_TYPE_BUSINESS;

  existingEmailData$$: BehaviorSubject<{
    templateId: string;
    subjectText: string;
    templateName: string;
  }> = new BehaviorSubject<{ templateId: string; subjectText: string; templateName: string }>(undefined);

  protected readonly previewImageUrlLoading = signal(false);
  protected readonly previewImageUrl$ = this.existingEmailData$$.pipe(
    tap((_) => this.previewImageUrlLoading.set(true)),
    switchMap((email) => {
      return this.getPreviewImageUrl$(email.templateId);
    }),
    retry({ count: 20, delay: 3000 }),
    catchError((_) => {
      this.snackbarService.openErrorSnack(
        'AUTOMATIONS.EDITOR.TASKS.SEND_HTML_EMAIL_TO_USER.ERROR.PREVIEW_EMAIL_FAILED',
      );
      this.previewImageUrlLoading.set(false);
      return of('');
    }),
  );

  isTemplateView$ = this.automationService.isTemplateView();

  getRules(): RuleInterface[] {
    return [];
  }

  getData(): any {
    const jsonData = this.step ? JSON.parse(this.step.data) : {};
    const existingData = this.existingEmailData$$.getValue();
    jsonData.template_id = existingData.templateId;
    jsonData.subject_text = existingData.subjectText;
    jsonData.template_name = existingData.templateName;
    return jsonData;
  }

  getControlsToValidateOnSave(): (UntypedFormControl | UntypedFormGroup | UntypedFormArray)[] {
    return [];
  }

  isFormValid(): boolean {
    return !!this.existingEmailData$$.getValue();
  }

  getWorkflowIdOverride(): string | undefined {
    const existingEmailData = this.existingEmailData$$.getValue();
    return existingEmailData ? existingEmailData.templateId : undefined;
  }

  constructor(
    protected readonly snackbarService: SnackbarService,
    protected readonly translateService: TranslateService,
    private readonly dialog: MatDialog,
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly location: Location,
    private readonly dataService: EmailBuilderDataService,
    private automationService: AutomationViewService,
    private readonly automationContextService: AutomataContextService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.description = Descriptions?.[this.taskDefinitionId];
    if (this.activatedRoute.snapshot.queryParams['email_to_user_emailId']) {
      this.existingEmailData$$.next({
        subjectText: '',
        templateId: this.activatedRoute.snapshot.queryParams['email_to_user_emailId'],
        templateName: '',
      });
      this.editEmail();
    }
    if (this.step) {
      const parsed = JSON.parse(this.step.data);
      this.existingEmailData$$.next({
        subjectText: parsed.subject_text,
        templateId: parsed.template_id,
        templateName: parsed.template_name,
      });
    }
    this.isTemplateView$.pipe(take(1)).subscribe((isTemplateView) => {
      if (isTemplateView) {
        // Assign an empty struct to pass validation
        this.existingEmailData$$.next({} as { templateId: string; subjectText: string; templateName: string });
      }
    });
    this.savingEnabled = true;
    this.backUrl = this.router.url;
  }

  editEmail(): void {
    let emailID = this.existingEmailData$$.getValue()?.templateId;
    if (!emailID) {
      emailID = this.createNewTemplateId();
    }

    this.modifyQueryParams(this.EMAIL_ID_QUERYSTRING_KEY, emailID);

    this.subscriptions.push(
      this.openEmailEditor(super.getAutomation().namespace, super.getAutomation().marketId, emailID)
        .afterClosed()
        .subscribe(([templateName, templateSubject]) => {
          if (templateName && templateSubject) {
            this.existingEmailData$$.next({
              templateId: emailID,
              templateName: templateName,
              subjectText: templateSubject,
            });
          }
          this.modifyQueryParams(this.EMAIL_ID_QUERYSTRING_KEY, '');
        }),
    );
  }

  async handleSelectExistingEmail(): Promise<void> {
    const dialogRef = this.dialog.open(PageComponent, {
      data: {
        openAsReadOnly: true,
        ownerId: super.getAutomation().namespace,
        ownerType: this.emailOwnerType,
        showPageActions: true,
      },
      height: '75%',
      width: '75%',
    });

    const email: Template = await firstValueFrom(dialogRef.afterClosed());
    if (email) {
      const newId = this.createNewTemplateId();
      const jsonData = email.templateJson ? JSON.parse(email.templateJson) : {};
      const creationSuccess = await firstValueFrom(
        this.dataService
          .createNewTemplate$(
            this.emailOwnerType,
            super.getAutomation()?.namespace ?? '',
            AppNamespace.AUTOMATIONS,
            newId,
            jsonData,
            email?.templateHtml ?? '',
            asReference(email),
          )
          .pipe(
            catchError((_) => {
              this.snackbarService.openErrorSnack('COMMON.ERROR_TRY_AGAIN');
              return of(false);
            }),
          ),
      );
      if (creationSuccess) {
        this.existingEmailData$$.next({
          templateId: newId,
          templateName: email.title,
          subjectText: email.templateSubject,
        });
      }
    }
  }

  removeSelectedEmail(): void {
    this.existingEmailData$$ = new BehaviorSubject<{ templateId: string; subjectText: string; templateName: string }>(
      undefined,
    );
  }

  emailIsEditable(): boolean {
    // Editable if automation is neither running nor draining
    return !(this.automation.runningState === 1 || this.automation.runningState === 3);
  }

  private getPreviewImageUrl$(emailId: string): Observable<string> {
    return this.dataService
      .getPreviewImageUrl$(this.emailOwnerType, super.getAutomation().namespace, AppNamespace.AUTOMATIONS, emailId)
      .pipe(
        map((url) => {
          if (url) {
            this.previewImageUrlLoading.set(false);
            return url;
          }
          throw new Error('Unable to retrieve preview image');
        }),
      );
  }

  private openEmailEditor(
    ownerId?: string,
    marketId?: string,
    emailId?: string,
  ): MatDialogRef<EmailEditorDialogComponent> {
    if (!ownerId) {
      ownerId = super.getAutomation().namespace;
    }
    if (!marketId) {
      marketId = super.getAutomation().marketId;
    }

    return this.dialog.open(EmailEditorDialogComponent, {
      data: {
        ownerId: ownerId,
        marketId: marketId,
        isNew: !this.existingEmailData$$.getValue(),
        emailId: emailId,
      },
      maxWidth: '100vw',
      maxHeight: '100vh',
      height: '100%',
      width: '100%',
    });
  }

  private createNewTemplateId(): string {
    return uuidv4();
  }

  private modifyQueryParams(key: string, value: string): void {
    const url = this.router
      .createUrlTree([], { relativeTo: this.activatedRoute, queryParams: { [key]: value } })
      .toString();
    this.location.go(url);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
